<!-- Dropdown control: distinct values from one field become the available
     options and the selected value is applied to the chosen targets. -->
<div class="wf-section" *ngIf="active">
  <header class="wf-section-header">
    <h3>{{ host.layoutSectionTitle('dropdown', 'Dropdown settings') }}</h3>
  </header>
  <small class="control-hint">
    {{ host.layoutSectionHint('dropdown', 'Choose the field that supplies the options and the dashboard components this dropdown controls.') }}
  </small>
  <ng-container *ngTemplateOutlet="datasetPicker"></ng-container>

  <div
    class="control-group control-group-full wf-field-drop-zone"
    *ngIf="host.fieldEnabled('dropdown', 'field')"
    (dragover)="host.allowFieldDrop($event)"
    (drop)="onDropdownFieldDrop($event)"
    title="Drag a dataset column here"
  >
    <label>{{ host.fieldLabel('dropdown', 'field', 'Option field') }}</label>
    <ng-select
      #ssDropdownField
      class="ss-select"
      [class.is-open]="!!ssDropdownField.isOpen?.()"
      appendTo="body"
      ngClass="ss-dropdown-panel"
      [items]="host.drillDatasetColumns"
      [clearable]="true"
      [searchable]="true"
      [loading]="host.isDrillDatasetColumnsLoading"
      [disabled]="!host.drillDataset"
      [placeholder]="host.drillColumnPlaceholder('dimension')"
      [(ngModel)]="dropdownField"
    ></ng-select>
  </div>

  <div class="control-row">
    <div class="control-group" *ngIf="host.fieldEnabled('dropdown', 'displayLabel')">
      <label>{{ host.fieldLabel('dropdown', 'displayLabel', 'Display label') }}</label>
      <input type="text" [(ngModel)]="dropdownDisplayLabel" placeholder="e.g. Region" />
    </div>
    <div class="control-group" *ngIf="host.fieldEnabled('dropdown', 'defaultValue')">
      <label>{{ host.fieldLabel('dropdown', 'defaultValue', 'Default value') }}</label>
      <input type="text" [(ngModel)]="dropdownDefaultValue" placeholder="Optional" />
    </div>
  </div>

  <!-- Display as: classic select vs always-visible filter buttons/tiles. -->
  <div class="control-group control-group-full" *ngIf="host.fieldEnabled('dropdown', 'displayMode')">
    <label>{{ host.fieldLabel('dropdown', 'displayMode', 'Display as') }}</label>
    <ng-select
      #ssDropdownDisplayMode
      class="ss-select"
      [class.is-open]="!!ssDropdownDisplayMode.isOpen?.()"
      appendTo="body"
      ngClass="ss-dropdown-panel"
      [items]="dropdownDisplayModes"
      bindLabel="label"
      bindValue="value"
      [clearable]="true"
      [searchable]="false"
      [(ngModel)]="dropdownDisplayMode"
    ></ng-select>
  </div>

  <!-- Submenu groups (buttons/tiles only): nest option values under a
       parent pill — clicking the parent opens its values as an inline
       submenu with an "All <parent>" entry. Values are validated against
       the live backend options at render time, so stale entries drop. -->
  <div
    class="control-group control-group-full"
    *ngIf="host.fieldEnabled('dropdown', 'optionGroups') && (dropdownDisplayMode === 'buttons' || dropdownDisplayMode === 'tiles')"
  >
    <label>{{ host.fieldLabel('dropdown', 'optionGroups', 'Submenu groups (optional)') }}</label>
    <small class="control-hint">
      Group option values under a parent — e.g. put MARI and MXDR inside Mining. The submenu shows exactly the values you pick; if the parent is itself an option value, add it to "Values inside" to keep it selectable. You can type values that aren't listed yet.
    </small>
    <div class="dropdown-group-card" *ngFor="let group of dropdownGroups; let i = index">
      <div class="dropdown-group-card-header">
        <span>{{ group.displayLabel || group.label || 'Group ' + (i + 1) }}</span>
        <button
          type="button"
          class="dropdown-group-remove"
          title="Remove this group"
          (click)="removeDropdownGroup(i)"
        >&times;</button>
      </div>
      <div class="dropdown-group-field">
        <label>Parent</label>
        <ng-select
          class="ss-select"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="dropdownFieldOptions"
          [addTag]="dropdownGroupTag"
          [clearable]="true"
          [searchable]="true"
          placeholder="e.g. Mining"
          [(ngModel)]="group.label"
        ></ng-select>
      </div>
      <!-- Display-only pill text; matching stays on Parent (the value). -->
      <div class="dropdown-group-field">
        <label>Display label (optional)</label>
        <input type="text" [(ngModel)]="group.displayLabel" placeholder="Shown on the pill — defaults to Parent" />
      </div>
      <div class="dropdown-group-field">
        <label>Values inside</label>
        <ng-select
          class="ss-select ss-select--multi ss-select--full"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="dropdownFieldOptions"
          [addTag]="dropdownGroupTag"
          [multiple]="true"
          [closeOnSelect]="false"
          [hideSelected]="true"
          [clearable]="true"
          [searchable]="true"
          placeholder="e.g. MARI, MXDR"
          [(ngModel)]="group.children"
        ></ng-select>
      </div>
      <label class="checkbox-label dropdown-group-showall" *ngIf="host.fieldEnabled('dropdown', 'groupShowAll')">
        <input type="checkbox" [(ngModel)]="group.showAll" />
        Show "All {{ group.displayLabel || group.label || 'group' }}" entry in the submenu
      </label>
    </div>
    <button type="button" class="target-reference-add" (click)="addDropdownGroup()">+ Add group</button>
  </div>

  <div class="control-group control-group-full" *ngIf="host.fieldEnabled('dropdown', 'targetCharts')">
    <label>{{ host.fieldLabel('dropdown', 'targetCharts', 'Target chart(s) / input(s)') }}</label>
    <ng-select
      #ssDropdownTargets
      class="ss-select ss-select--multi ss-select--full"
      [class.is-open]="!!ssDropdownTargets.isOpen?.()"
      appendTo="body"
      ngClass="ss-dropdown-panel"
      [items]="host.targetCharts"
      bindLabel="label"
      bindValue="value"
      [multiple]="true"
      [closeOnSelect]="false"
      [hideSelected]="false"
      [clearable]="true"
      [searchable]="true"
      placeholder="Select the chart(s) or input(s) this dropdown filters"
      [(ngModel)]="host.selectedTargetCharts"
    ></ng-select>
    <small class="control-hint" *ngIf="!host.targetCharts.length">Add a chart or input to the dashboard first so it can be selected as a target.</small>
  </div>
  <ng-container *ngTemplateOutlet="apiCallConfigBlock; context: { kind: 'dropdown' }"></ng-container>

  <div class="control-group control-group-full" *ngIf="host.fieldEnabled('dropdown', 'isMultiSelect')">
    <label class="checkbox-label">
      <input type="checkbox" [(ngModel)]="dropdownIsMultiSelect" />
      {{ host.fieldLabel('dropdown', 'isMultiSelect', 'Allow multiple selections') }}
    </label>
  </div>
  <div class="control-group control-group-full" *ngIf="host.fieldEnabled('dropdown', 'showAllOption')">
    <label class="checkbox-label">
      <input type="checkbox" [(ngModel)]="dropdownShowAllOption" />
      {{ host.fieldLabel('dropdown', 'showAllOption', 'Show All option') }}
    </label>
  </div>

  <div
    class="dvm-format-group"
    [class.is-open]="dropdownAdvancedAccordionOpen"
    *ngIf="host.fieldEnabled('dropdownAdvanced', 'optionOrderMode') || host.fieldEnabled('dropdownAdvanced', 'customOptionOrder')"
  >
    <button type="button" class="dvm-format-group-head" (click)="dropdownAdvancedAccordionOpen = !dropdownAdvancedAccordionOpen">
      <span>{{ host.layoutSectionTitle('dropdownAdvanced', 'Advanced') }}</span>
      <mat-icon>{{ dropdownAdvancedAccordionOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
    </button>
    <div class="dvm-format-group-body" *ngIf="dropdownAdvancedAccordionOpen">
    <small class="control-hint">{{ host.layoutSectionHint('dropdownAdvanced', '') }}</small>

    <div class="control-group control-group-full" *ngIf="host.fieldEnabled('dropdownAdvanced', 'optionOrderMode')">
      <label>{{ host.fieldLabel('dropdownAdvanced', 'optionOrderMode', 'Option order') }}</label>
      <ng-select
        #ssDropdownOptionOrder
        class="ss-select"
        [class.is-open]="!!ssDropdownOptionOrder.isOpen?.()"
        appendTo="body"
        ngClass="ss-dropdown-panel"
        [items]="dropdownOptionOrderModes"
        bindLabel="label"
        bindValue="value"
        [clearable]="true"
        [searchable]="false"
        [(ngModel)]="dropdownOptionOrderMode"
      ></ng-select>
    </div>

    <div class="control-group control-group-full" *ngIf="host.fieldEnabled('dropdownAdvanced', 'customOptionOrder') && dropdownOptionOrderMode === 'custom'">
      <label>{{ host.fieldLabel('dropdownAdvanced', 'customOptionOrder', 'Custom option order') }}</label>
      <input type="text" [(ngModel)]="dropdownCustomOptionOrder" placeholder="e.g. High, Medium, Low" />
    </div>
    </div>
  </div>

  <!-- Appearance accordion — the SHARED config-driven format accordion
       (formatControls appliesTo DROPDOWN: pill colors / radius / font
       size). Same values as the tile's Format popup; live-applied via
       onBarFormatChange → foldFormatIntoEditingDropdownTile. -->
  <ng-container *ngTemplateOutlet="chartFormatAccordion"></ng-container>
</div>
